// Notification sound utility
class NotificationSound {
  private audioContext: AudioContext | null = null;
  private isEnabled: boolean = true;

  constructor() {
    // Check if user has previously disabled sounds
    const soundEnabled = localStorage.getItem('notificationSoundEnabled');
    this.isEnabled = soundEnabled !== 'false';
  }

  // Initialize audio context (required for modern browsers)
  private initAudioContext(): AudioContext | null {
    if (this.audioContext) {
      return this.audioContext;
    }

    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      return this.audioContext;
    } catch (_error) {
      // console.warn('Audio context not supported:', error);
      return null;
    }
  }

  // Play a simple notification sound
  playNotificationSound(): void {
    if (!this.isEnabled) {
      return;
    }

    const audioContext = this.initAudioContext();
    if (!audioContext) {
      return;
    }

    try {
      // Create a simple beep sound
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Configure the sound
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // 800 Hz
      oscillator.type = 'sine';

      // Set volume envelope
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

      // Play the sound
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);

      // Play a second beep for emphasis
      setTimeout(() => {
        const oscillator2 = audioContext.createOscillator();
        const gainNode2 = audioContext.createGain();

        oscillator2.connect(gainNode2);
        gainNode2.connect(audioContext.destination);

        oscillator2.frequency.setValueAtTime(1000, audioContext.currentTime); // 1000 Hz
        oscillator2.type = 'sine';

        gainNode2.gain.setValueAtTime(0, audioContext.currentTime);
        gainNode2.gain.linearRampToValueAtTime(0.2, audioContext.currentTime + 0.01);
        gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);

        oscillator2.start(audioContext.currentTime);
        oscillator2.stop(audioContext.currentTime + 0.2);
      }, 150);
    } catch (_error) {
      // console.warn('Error playing notification sound:', error);
    }
  }

  // Enable/disable notification sounds
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    localStorage.setItem('notificationSoundEnabled', enabled.toString());
  }

  // Check if sounds are enabled
  isSoundEnabled(): boolean {
    return this.isEnabled;
  }

  // Play a different sound for different notification types
  playSoundForType(type: string): void {
    if (!this.isEnabled) {
      return;
    }

    const audioContext = this.initAudioContext();
    if (!audioContext) {
      return;
    }

    try {
      let frequency = 800; // Default frequency
      let duration = 0.3;

      // Different sounds for different notification types
      switch (type) {
        case 'mention':
          frequency = 1000; // Higher pitch for mentions
          duration = 0.4;
          break;
        case 'task_assignment':
          frequency = 600; // Lower pitch for task assignments
          duration = 0.5;
          break;
        case 'gst_reminder':
          frequency = 900; // Medium pitch for reminders
          duration = 0.3;
          break;
        case 'system_alert':
          frequency = 1200; // High pitch for alerts
          duration = 0.6;
          break;
      }

      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + duration);
    } catch (_error) {
      // console.warn('Error playing notification sound:', error);
    }
  }
}

// Export a singleton instance
export const notificationSound = new NotificationSound();
